import { useState, useEffect, useCallback } from "react";

const API_BASE = "https://wxeqs3mwub.execute-api.eu-west-1.amazonaws.com/prod";
const COGNITO_DOMAIN = "https://story-journal-736971699050.auth.eu-west-1.amazoncognito.com";
const COGNITO_CLIENT_ID = "542b363plf18tksser0cd4bvmj";
const COGNITO_REDIRECT = window.location.origin;

const SUGGESTED_TAGS = [
  "ownership", "accountability", "scaling", "zero-to-one", "AI strategy",
  "hard decision", "hiring", "culture", "conflict", "executive influence",
  "team building", "product strategy", "constraint", "failure", "customer impact",
  "negotiation", "ambiguity", "cross-functional", "turnaround", "innovation"
];

function getTokensFromHash() {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  return { idToken: params.get("id_token"), accessToken: params.get("access_token") };
}

function getStoredToken() {
  const token = localStorage.getItem("id_token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp * 1000 < Date.now()) {
      localStorage.removeItem("id_token");
      return null;
    }
    return token;
  } catch {
    localStorage.removeItem("id_token");
    return null;
  }
}

function loginUrl() {
  return `${COGNITO_DOMAIN}/login?client_id=${COGNITO_CLIENT_ID}&response_type=token&scope=openid+email&redirect_uri=${encodeURIComponent(COGNITO_REDIRECT)}`;
}

function logoutUrl() {
  return `${COGNITO_DOMAIN}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(COGNITO_REDIRECT)}`;
}

async function api(method, path, body) {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    localStorage.removeItem("id_token");
    window.location.href = loginUrl();
    return;
  }
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch { /* not JSON */ }
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

function RequestAccessModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setStatus("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    setStatus("");
    try {
      const res = await fetch(`${API_BASE}/request-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, note: note.trim() }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSubmitted(true);
    } catch (e) {
      setStatus(`Could not send request: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#fff", padding: 24, borderRadius: 8, width: 360, fontFamily: "system-ui, sans-serif" }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginTop: 0, marginBottom: 8, color: "#111" }}>Request access</h2>
        {submitted ? (
          <>
            <p style={{ fontSize: 13, color: "#374151", marginBottom: 16 }}>
              Thanks — your request was sent. You'll get an email with a temporary password once it's approved.
            </p>
            <div style={{ textAlign: "right" }}>
              <button onClick={onClose} style={{ padding: "6px 14px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>Story Journal is invite-only. Drop your email and we'll get back to you.</p>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginBottom: 10, border: "0.5px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
            />
            <textarea
              placeholder="Optional note (who you are, why you'd like access)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              style={{ width: "100%", padding: "8px 10px", marginBottom: 12, border: "0.5px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical" }}
            />
            {status && <p style={{ fontSize: 12, color: "#dc2626", margin: "0 0 12px" }}>{status}</p>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={onClose} disabled={submitting}
                style={{ padding: "6px 14px", background: "none", color: "#6b7280", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                style={{ padding: "6px 14px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, cursor: submitting ? "not-allowed" : "pointer", fontSize: 13, opacity: submitting ? 0.6 : 1 }}>
                {submitting ? "Sending..." : "Send request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [status, setStatus] = useState("");

  const handleSubmit = async () => {
    if (!oldPw || !newPw) { setStatus("Please fill in all fields."); return; }
    if (newPw !== confirmPw) { setStatus("New passwords don't match."); return; }
    if (newPw.length < 8) { setStatus("Password must be at least 8 characters."); return; }
    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        setStatus("Session missing access token. Please sign out and sign in again.");
        return;
      }
      const res = await fetch(`https://cognito-idp.eu-west-1.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.ChangePassword",
        },
        body: JSON.stringify({
          PreviousPassword: oldPw,
          ProposedPassword: newPw,
          AccessToken: accessToken,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to change password");
      }
      setStatus("Password changed successfully!");
      setTimeout(onClose, 1500);
    } catch (e) {
      setStatus(e.message);
    }
  };

  const inputStyle = { width: "100%", marginBottom: 10, padding: "8px 10px", border: "0.5px solid #e5e7eb", borderRadius: 6, fontSize: 13 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", width: 360, maxWidth: "90vw" }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: "0 0 16px", color: "#111" }}>Change password</p>
        <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} placeholder="Current password" style={inputStyle} />
        <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password" style={inputStyle} />
        <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm new password" style={inputStyle} />
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button onClick={handleSubmit}
            style={{ padding: "7px 16px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
            Change password
          </button>
          <button onClick={onClose}
            style={{ padding: "7px 16px", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 13, background: "none", color: "#374151" }}>
            Cancel
          </button>
        </div>
        {status && <p style={{ fontSize: 12, color: status.includes("successfully") ? "#059669" : "#dc2626", marginTop: 8 }}>{status}</p>}
      </div>
    </div>
  );
}

function TagPill({ tag, onRemove, onClick }) {
  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "#e8f0fe", color: "#1a56db", borderRadius: 6,
        padding: "2px 8px", fontSize: 12, cursor: onClick ? "pointer" : "default",
        marginRight: 4, marginBottom: 4
      }}
    >
      {tag}
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#1a56db", padding: 0, fontSize: 14, lineHeight: 1 }}>
          ×
        </button>
      )}
    </span>
  );
}

function AdminDashboardModal({ onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api("GET", "/admin/usage")
      .then(setData)
      .catch(e => setError(e.message));
  }, []);

  const grouped = (() => {
    if (!data) return [];
    const byPeriod = new Map();
    for (const r of data.rows) {
      if (!byPeriod.has(r.period)) byPeriod.set(r.period, []);
      byPeriod.get(r.period).push(r);
    }
    return [...byPeriod.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  })();

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", width: 720, maxWidth: "92vw", maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ fontWeight: 500, fontSize: 14, margin: 0, color: "#111" }}>Admin · usage by user</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#9ca3af" }}>×</button>
        </div>
        {error && <p style={{ fontSize: 13, color: "#dc2626" }}>{error}</p>}
        {!data && !error && <p style={{ fontSize: 13, color: "#9ca3af" }}>Loading...</p>}
        {data && (
          <>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px" }}>{data.totalUsers} total users · monthly polish limit {data.limit}</p>
            {grouped.length === 0 && <p style={{ fontSize: 13, color: "#9ca3af" }}>No usage recorded yet.</p>}
            {grouped.map(([period, rows]) => (
              <div key={period} style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: "#374151", margin: "0 0 8px" }}>{period}</p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: "0.5px solid #e5e7eb", color: "#6b7280", textAlign: "left" }}>
                      <th style={{ padding: "6px 8px", fontWeight: 500 }}>User</th>
                      <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Polishes</th>
                      <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Stories</th>
                      <th style={{ padding: "6px 8px", fontWeight: 500 }}>Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.sort((a, b) => b.polish - a.polish).map(r => (
                      <tr key={r.userId + r.period} style={{ borderBottom: "0.5px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 8px", color: "#111" }}>{r.email}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: r.polish >= data.limit ? "#dc2626" : r.polish >= data.limit * 0.7 ? "#d97706" : "#374151" }}>
                          {r.polish} / {data.limit}
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: "#374151" }}>{r.create}</td>
                        <td style={{ padding: "6px 8px", color: "#9ca3af" }}>{r.lastCallAt ? new Date(r.lastCallAt).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function PolishModal({ entry, onClose, onSave, onPolished }) {
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api("POST", "/polish", { title: entry.title, text: entry.text, tags: entry.tags })
      .then(data => { setResult(data.polished); onPolished?.(data.usage); })
      .catch(e => { setError(e.message); })
      .finally(() => setLoading(false));
  }, [entry, onPolished]);

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    try {
      await api("PUT", `/entries/${entry.id}`, { title: entry.title, text: entry.text, tags: entry.tags, polished: result });
      setSaved(true);
      onSave();
    } catch (e) {
      setError("Error saving: " + e.message);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", width: 600, maxWidth: "90vw", maxHeight: "80vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <p style={{ fontWeight: 500, fontSize: 14, margin: 0, color: "#111" }}>Polished for interview</p>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#9ca3af" }}>×</button>
        </div>
        {loading && <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "2rem 0" }}>Polishing your story...</p>}
        {error && <p style={{ fontSize: 13, color: "#dc2626", padding: "1rem 0" }}>{error}</p>}
        {result && (
          <>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap", background: "#f9fafb",
              border: "0.5px solid #e5e7eb", borderRadius: 8, padding: "1rem" }}>{result}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={handleSave} disabled={saved}
                style={{ padding: "7px 16px", background: saved ? "#059669" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, cursor: saved ? "default" : "pointer", fontSize: 13 }}>
                {saved ? "Saved!" : "Save polished version"}
              </button>
              <button onClick={handleCopy}
                style={{ padding: "7px 16px", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 13, background: "none", color: "#374151" }}>
                {copied ? "Copied!" : "Copy to clipboard"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StoryCard({ entry, onEdit, onTagClick, searchQuery, onPolish }) {
  const [showPolished, setShowPolished] = useState(false);
  const highlight = (text) => {
    if (!searchQuery) return text;
    const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
    return parts.map((p, i) =>
      p.toLowerCase() === searchQuery.toLowerCase()
        ? <mark key={i} style={{ background: "#fef08a", borderRadius: 2 }}>{p}</mark>
        : p
    );
  };
  const date = new Date(entry.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const preview = entry.text.length > 300 ? entry.text.slice(0, 300) + "..." : entry.text;

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: 0, color: "#111" }}>{highlight(entry.title)}</p>
        <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap", marginLeft: 8 }}>{date}</span>
      </div>
      <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, margin: "0 0 8px" }}>{highlight(preview)}</p>
      {entry.polished && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => setShowPolished(!showPolished)}
            style={{ fontSize: 12, color: "#1d4ed8", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 4 }}>
            {showPolished ? "Hide polished version" : "Show polished version"}
          </button>
          {showPolished && (
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap", background: "#f0fdf4",
              border: "0.5px solid #bbf7d0", borderRadius: 8, padding: "0.75rem", marginTop: 4 }}>
              {entry.polished}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 8 }}>
        {entry.tags.map(t => (
          <TagPill key={t} tag={t} onClick={() => onTagClick(t)} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => onEdit(entry)}
          style={{ padding: "5px 12px", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 12, background: "none", color: "#374151" }}>
          Edit
        </button>
        <button onClick={() => onPolish(entry)}
          style={{ padding: "5px 12px", border: "0.5px solid #bfdbfe", borderRadius: 6, cursor: "pointer", fontSize: 12, background: "#eff6ff", color: "#1d4ed8" }}>
          Polish for interview
        </button>
      </div>
    </div>
  );
}

function WriteTab({ onSave }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);
  const [status, setStatus] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  const loadForEdit = useCallback((entry) => {
    setTitle(entry.title);
    setText(entry.text);
    setTags(entry.tags);
    setEditingEntry(entry);
  }, []);

  useEffect(() => {
    window._loadStoryForEdit = loadForEdit;
  }, [loadForEdit]);

  const addTag = (tag) => {
    if (tag && !tags.includes(tag)) setTags([...tags, tag]);
  };

  const handleTagKey = (e) => {
    if (e.key === "Enter") {
      addTag(tagInput.trim());
      setTagInput("");
    }
  };

  const toggleSpeech = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setStatus("Speech recognition not supported in this browser."); return; }
    if (isRecording) {
      recognition?.stop();
      setIsRecording(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    const base = text;
    rec.onresult = (e) => {
      let final = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      setText(base + (base ? " " : "") + final + interim);
    };
    rec.onend = () => { setIsRecording(false); };
    rec.onerror = (e) => { setStatus("Mic error: " + e.error); setIsRecording(false); };
    rec.start();
    setRecognition(rec);
    setIsRecording(true);
    setStatus("");
  };

  const handleSave = async () => {
    if (!title.trim() || !text.trim()) { setStatus("Please add a title and story."); return; }
    try {
      if (editingEntry) {
        await api("PUT", `/entries/${editingEntry.id}`, { title, text, tags });
        setStatus("Story updated.");
      } else {
        await api("POST", "/entries", { title, text, tags });
        setStatus("Story saved.");
      }
      setTitle(""); setText(""); setTags([]); setTagInput(""); setEditingEntry(null);
      onSave();
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      setStatus("Error saving: " + e.message);
    }
  };

  const handleDelete = async () => {
    if (!editingEntry) return;
    if (!window.confirm("Delete this story?")) return;
    try {
      await api("DELETE", `/entries/${editingEntry.id}`);
      setTitle(""); setText(""); setTags([]); setEditingEntry(null);
      setStatus("Story deleted.");
      onSave();
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      setStatus("Error deleting: " + e.message);
    }
  };

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 12, padding: "1rem 1.25rem" }}>
      <p style={{ fontWeight: 500, fontSize: 14, margin: "0 0 12px", color: "#111" }}>
        {editingEntry ? "Edit story" : "New story"}
      </p>
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 4px" }}>Title</p>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. How I introduced revenue prioritization at Afero"
        style={{ width: "100%", marginBottom: 12, padding: "8px 10px", border: "0.5px solid #e5e7eb", borderRadius: 6, fontSize: 13 }} />
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 4px" }}>Story</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
        <button onClick={toggleSpeech}
          style={{ padding: "6px 12px", border: `0.5px solid ${isRecording ? "#fca5a5" : "#e5e7eb"}`,
            borderRadius: 6, cursor: "pointer", fontSize: 12, background: isRecording ? "#fef2f2" : "none",
            color: isRecording ? "#dc2626" : "#374151" }}>
          {isRecording ? "Stop dictating" : "Dictate"}
        </button>
        {isRecording && <span style={{ fontSize: 11, color: "#dc2626" }}>Listening...</span>}
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)}
        placeholder="Situation, what you did, outcome..."
        style={{ width: "100%", minHeight: 120, marginBottom: 12, padding: "8px 10px",
          border: "0.5px solid #e5e7eb", borderRadius: 6, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} />
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 6px" }}>Suggested tags</p>
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 10 }}>
        {SUGGESTED_TAGS.map(t => (
          <button key={t} onClick={() => addTag(t)}
            style={{ fontSize: 11, padding: "3px 10px", border: "0.5px solid #e5e7eb", borderRadius: 6,
              cursor: "pointer", color: "#6b7280", background: tags.includes(t) ? "#eff6ff" : "none",
              marginRight: 4, marginBottom: 4 }}>
            {t}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 6 }}>
        {tags.map(t => <TagPill key={t} tag={t} onRemove={() => setTags(tags.filter(x => x !== t))} />)}
      </div>
      <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={handleTagKey}
        placeholder="Add custom tag and press Enter"
        style={{ width: "100%", marginBottom: 12, padding: "8px 10px", border: "0.5px solid #e5e7eb", borderRadius: 6, fontSize: 13 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={handleSave}
          style={{ padding: "7px 16px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
          Save story
        </button>
        <button onClick={() => { setTitle(""); setText(""); setTags([]); setEditingEntry(null); setStatus(""); }}
          style={{ padding: "7px 16px", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 13, background: "none", color: "#374151" }}>
          Clear
        </button>
        {editingEntry && (
          <button onClick={handleDelete}
            style={{ padding: "7px 16px", border: "0.5px solid #fca5a5", borderRadius: 6, cursor: "pointer", fontSize: 13, background: "none", color: "#dc2626" }}>
            Delete
          </button>
        )}
      </div>
      {status && <p style={{ fontSize: 12, color: status.includes("Error") ? "#dc2626" : "#059669", marginTop: 8 }}>{status}</p>}
    </div>
  );
}

function SearchTab({ entries, onEdit, onPolish }) {
  const [query, setQuery] = useState("");

  const results = query.trim()
    ? entries.filter(e =>
        e.title.toLowerCase().includes(query.toLowerCase()) ||
        e.text.toLowerCase().includes(query.toLowerCase()) ||
        e.tags.some(t => t.toLowerCase().includes(query.toLowerCase()))
      )
    : [];

  return (
    <div>
      <input value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Search by keyword, topic, or tag..."
        style={{ width: "100%", marginBottom: 16, padding: "8px 10px", border: "0.5px solid #e5e7eb", borderRadius: 6, fontSize: 13 }} />
      {query && results.length === 0 && <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "2rem 0" }}>No stories found.</p>}
      {results.length > 0 && (
        <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>{results.length} story{results.length > 1 ? "ies" : ""} found</p>
      )}
      {results.map(e => (
        <StoryCard key={e.id} entry={e} onEdit={onEdit} onTagClick={(t) => setQuery(t)} searchQuery={query} onPolish={onPolish} />
      ))}
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState("write");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => getStoredToken());

  const [showChangePw, setShowChangePw] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [polishEntry, setPolishEntry] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    localStorage.removeItem("ai_provider");
    localStorage.removeItem("ai_api_key");
  }, []);

  useEffect(() => {
    const { idToken, accessToken } = getTokensFromHash();
    if (idToken) {
      localStorage.setItem("id_token", idToken);
      if (accessToken) localStorage.setItem("access_token", accessToken);
      setToken(idToken);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    if (!getStoredToken()) return;
    try { setUsage(await api("GET", "/usage")); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage, token]);

  const fetchEntries = useCallback(async () => {
    if (!getStoredToken()) return;
    try {
      const data = await api("GET", "/entries");
      setEntries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries, token]);

  if (!token) {
    return (
      <div style={{ maxWidth: 400, margin: "4rem auto", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 8, color: "#111" }}>Story Journal</h1>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>Sign in to access your stories.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <a href={loginUrl()}
            style={{ display: "inline-block", padding: "10px 24px", background: "#1d4ed8", color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 14 }}>
            Sign in
          </a>
          <button onClick={() => setShowRequestAccess(true)}
            style={{ padding: "10px 24px", background: "#fff", color: "#1d4ed8", border: "0.5px solid #1d4ed8", borderRadius: 8, cursor: "pointer", fontSize: 14, fontFamily: "inherit" }}>
            Request access
          </button>
        </div>
        {showRequestAccess && <RequestAccessModal onClose={() => setShowRequestAccess(false)} />}
      </div>
    );
  }

  const handleEdit = (entry) => {
    setTab("write");
    setTimeout(() => window._loadStoryForEdit?.(entry), 50);
  };

  const tabStyle = (t) => ({
    padding: "6px 16px", border: "0.5px solid",
    borderColor: tab === t ? "#1d4ed8" : "#e5e7eb",
    borderRadius: 6, cursor: "pointer", fontSize: 13,
    background: tab === t ? "#eff6ff" : "none",
    color: tab === t ? "#1d4ed8" : "#6b7280"
  });

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "1.5rem 1rem", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 500, margin: "0 0 4px", color: "#111" }}>Story journal</h1>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 4px" }}>{entries.length} stories saved</p>
          {usage && (
            <p style={{ fontSize: 12, color: usage.polish.used >= usage.polish.limit ? "#dc2626" : usage.polish.used >= usage.polish.limit * 0.7 ? "#d97706" : "#9ca3af", margin: "0 0 16px" }}>
              Polishes this month: {usage.polish.used} / {usage.polish.limit}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {usage?.isAdmin && (
            <button onClick={() => setShowAdmin(true)}
              style={{ padding: "5px 12px", border: "0.5px solid #1d4ed8", borderRadius: 6, cursor: "pointer", fontSize: 12, background: "none", color: "#1d4ed8" }}>
              Admin
            </button>
          )}
          <button onClick={() => setShowChangePw(true)}
            style={{ padding: "5px 12px", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 12, background: "none", color: "#6b7280" }}>
            Change password
          </button>
          <button onClick={() => { localStorage.removeItem("id_token"); localStorage.removeItem("access_token"); window.location.href = logoutUrl(); }}
            style={{ padding: "5px 12px", border: "0.5px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontSize: 12, background: "none", color: "#6b7280" }}>
            Sign out
          </button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button style={tabStyle("write")} onClick={() => setTab("write")}>Write</button>
        <button style={tabStyle("search")} onClick={() => setTab("search")}>Search</button>
        <button style={tabStyle("browse")} onClick={() => setTab("browse")}>All stories</button>
      </div>
      {tab === "write" && <WriteTab onSave={fetchEntries} />}
      {tab === "search" && <SearchTab entries={entries} onEdit={handleEdit} onPolish={setPolishEntry} />}
      {tab === "browse" && (
        <div>
          {loading && <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "2rem 0" }}>Loading...</p>}
          {!loading && entries.length === 0 && <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "2rem 0" }}>No stories yet. Write your first one.</p>}
          {!loading && entries.length > 0 && (() => {
            const allTags = [...new Set(entries.flatMap(e => e.tags))].sort();
            const filtered = filterTag ? entries.filter(e => e.tags.includes(filterTag)) : entries;
            return (
              <>
                {allTags.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px" }}>Filter by tag</p>
                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                      {allTags.map(t => (
                        <button key={t} onClick={() => setFilterTag(filterTag === t ? null : t)}
                          style={{ fontSize: 11, padding: "3px 10px", border: "0.5px solid",
                            borderColor: filterTag === t ? "#1d4ed8" : "#e5e7eb", borderRadius: 6,
                            cursor: "pointer", color: filterTag === t ? "#1d4ed8" : "#6b7280",
                            background: filterTag === t ? "#eff6ff" : "none", marginRight: 4, marginBottom: 4 }}>
                          {t}
                        </button>
                      ))}
                    </div>
                    {filterTag && (
                      <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>{filtered.length} story{filtered.length !== 1 ? "ies" : ""} tagged "{filterTag}"</p>
                    )}
                  </div>
                )}
                {filtered.map(e => <StoryCard key={e.id} entry={e} onEdit={handleEdit} onTagClick={(t) => setFilterTag(filterTag === t ? null : t)} searchQuery="" onPolish={setPolishEntry} />)}
              </>
            );
          })()}
        </div>
      )}
      {polishEntry && <PolishModal entry={polishEntry} onClose={() => setPolishEntry(null)} onSave={fetchEntries} onPolished={fetchUsage} />}
      {showAdmin && <AdminDashboardModal onClose={() => setShowAdmin(false)} />}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
